import React, { useEffect, useState } from 'react';
import { UncontrolledDropdown, DropdownToggle } from 'reactstrap';
import {
  Text,
  WarningOctagonIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  StyledDropdownMenu,
  StyledDropdownItem,
} from '@aag-development/becreatives-ui';
import {
  RevisionCursorNavLink,
  RevisionFallbackImage,
  RevisionFallbackWrapper,
  RevisionVersionControlButton,
  RevisionMainContentCard,
  RevisionVideoControlRow,
} from './RevisionsContentMain.styled';
import { If } from 'src/components/If';
import { RevisionVersionTooltipPopover } from './RevisionVersionTooltipPopover';
import { useTranslation } from 'react-i18next';
import { ContentGenerationButtons } from 'src/components/Button';
import { useRequestId } from 'src/features/requests/use-request-id';
import { MediaViewer } from 'src/components/MediaCard';
import { getPreferredSource, makeSourceOptions, useMediaDownload } from 'src/features/media';
import RevisionFileDisplayFallback from 'src/assets/images/revision-file-display-fallback.png';
import { RevisionMediaList } from 'src/features/revisions';
import { cn } from 'src/lib/utils';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { CardErrorFallback } from 'src/features/fallback';
import { Skeleton } from 'src/components/ui/skeleton';
import { MediaSignedUrlsQueryProvider } from 'src/features/media/use-media-signed-urls-query-context';
import { RevisionVideoPlayer } from 'src/features/revisions/revision-video-player';
import { AspectRatio } from 'src/components/ui/aspect-ratio';
import { useRequestRevisionsStore } from 'src/features/revisions/use-revisions-context-provider';
import { useDecoratedRequestContext } from 'src/features/requests/use-decorated-request-context';
import { RequestCommentsQueryContextProvider } from 'src/features/comments/use-request-comments';
import { EmptySpaceFallbackImage } from 'src/features/fallback/ui';
import {
  VideoPlayerContextProvider,
  VideoPlayerSkeleton,
} from 'src/features/video-player/video-player';
import { rateRevisionDialogPortalTarget } from 'src/features/revisions/rate-revision-composed-dialog';
import {
  RevisionChatContent,
  RevisionChatSection,
} from 'src/pages/RevisionsPage/revisions-content-main/revision-chat-content';
import {
  ChevronDown,
  CloudDownload,
  Loader2,
  Save,
  CheckCircle,
  Share2,
  Clipboard,
  Check,
  Sparkles,
} from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from 'src/components/ui/popover';
import {
  ArchivedMediaMessage,
  ReadyToDownloadMediaMessage,
  UnarchivedMediaMessage,
} from 'src/features/video-player/archived-media';
import {
  isMediaBeingUnarchived,
  isMediaNeedsUnarchiving,
  isMediaReadyForDownload,
} from './media-restoration.utils';
import {
  useRequestFilesArchivedMessage,
  useRequestFilesRestoringMessage,
} from 'src/features/requests/request-files-archived-copy';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { useSaveToMediaLibrary } from 'src/features/revisions/useSaveToMediaLibrary';
import { ButtonLoader } from 'src/components/ui/loader';
import { useToast } from 'src/components/ui/use-toast';
import { Api } from 'src/api';
import { Logger } from 'src/utils/logger';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button/button';
import { Input } from 'src/components/ui/input';

const RevisionsContentMain: React.FC = () => {
  const { t } = useTranslation('pages/shared');
  const requestId = useRequestId();

  const request = useDecoratedRequestContext();

  const {
    selectedRevision,
    selectedRevisionMedia,
    revisions,
    videoIsArchived,
    select,
    selectPrevious,
    selectNext,
    canSelectNext,
    canSelectPrevious,
  } = useRequestRevisionsStore();
  const { downloadAll, isDownloading } = useMediaDownload();
  const { saveToMediaLibrary, isSaving, isAlreadySaved, isAlreadySavedSync } =
    useSaveToMediaLibrary();
  const archivedMessage = useRequestFilesArchivedMessage();
  const restoringMessage = useRequestFilesRestoringMessage();

  // Cold-stored files cannot be fetched, so the download control has to follow
  // the file's storage state rather than sit there offering a transfer that
  // would fail (#2678). Both flags feed the placeholder below as well, so the
  // control and the player never disagree about which state the file is in.
  const filesNeedUnarchiving = isMediaNeedsUnarchiving(selectedRevisionMedia, videoIsArchived);
  const filesUnarchiving = isMediaBeingUnarchived(selectedRevisionMedia);
  const downloadBlocked = filesNeedUnarchiving || filesUnarchiving;

  /**
   * What the player area shows when the file is not in hot storage — null when
   * there is nothing to explain and the media itself can be rendered.
   *
   * Storage state is decided BEFORE file type on purpose. These three used to
   * be rendered inside the playable branch only, so a delivery whose selected
   * file is an image, a caption or a project file skipped them entirely and
   * fell through to MediaViewer, which draws archived media as a bare icon box.
   * The customer got a dead Download control next to an empty frame and no
   * sentence anywhere saying why. A cold file is cold whatever its mime type.
   */
  const restorationPlaceholder = isMediaReadyForDownload(selectedRevisionMedia) ? (
    <ReadyToDownloadMediaMessage
      downloadUrl={selectedRevisionMedia!.restoration_download_url!}
      expiryDate={selectedRevisionMedia!.restoration_expiry!}
    />
  ) : filesUnarchiving ? (
    <UnarchivedMediaMessage />
  ) : filesNeedUnarchiving ? (
    <ArchivedMediaMessage />
  ) : null;
  const [currentFileAlreadySaved, setCurrentFileAlreadySaved] = useState(false);
  const [shareUrl, setShareUrl] = useState<string>('');
  const [isLoadingShareUrl, setIsLoadingShareUrl] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const { toast } = useToast();

  // Check if current file is already saved whenever selectedRevisionMedia changes
  useEffect(() => {
    if (selectedRevisionMedia?.index_url) {
      // Simple sync check - no API calls to avoid errors
      const syncStatus = isAlreadySavedSync(selectedRevisionMedia);
      setCurrentFileAlreadySaved(syncStatus);
    } else {
      setCurrentFileAlreadySaved(false);
    }
  }, [selectedRevisionMedia?.id, selectedRevisionMedia?.file_name, isAlreadySavedSync]);

  // Generate share URL when media changes
  const generateShareUrl = async () => {
    if (!selectedRevisionMedia?.id) return;

    setIsLoadingShareUrl(true);
    try {
      const url = await Api.media.share(selectedRevisionMedia.id);
      setShareUrl(url);
      setIsShareModalOpen(true);
    } catch (error) {
      Logger.error('Failed to generate share URL', 'RevisionsContentMain', error);
      toast({
        description: t('Failed to generate share link'),
        variant: 'destructive',
      });
    } finally {
      setIsLoadingShareUrl(false);
    }
  };

  // Copy share URL to clipboard
  const copyShareUrl = async () => {
    if (!shareUrl) {
      await generateShareUrl();
      return;
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      setIsCopied(true);
      toast({
        description: t('Share link copied to clipboard!'),
        variant: 'info',
      });

      // Reset copied state after 2 seconds
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (error) {
      Logger.error('Failed to copy share URL', 'RevisionsContentMain', error);
      toast({
        description: t('Failed to copy share link'),
        variant: 'destructive',
      });
    }
  };

  // Open share modal
  const openShareModal = async () => {
    if (shareUrl) {
      setIsShareModalOpen(true);
    } else {
      await generateShareUrl();
    }
  };

  // Reset share URL when media changes
  useEffect(() => {
    setShareUrl('');
    setIsShareModalOpen(false);
    setIsCopied(false);
  }, [selectedRevisionMedia?.id]);

  return (
    <div className={'tw-mb-4 tw-grid tw-gap-4 tw-pt-2 md:tw-pt-0 lg:tw-grid-cols-12'}>
      <RevisionMainContentCard
        id={rateRevisionDialogPortalTarget}
        color={'white'}
        className={'tw-mb-0 tw-self-start lg:tw-col-span-7 xl:tw-col-span-8'}
      >
        {/*Content top*/}
        <RevisionVideoControlRow
          className={'tw-flex tw-w-full tw-flex-col tw-gap-2 xl:tw-flex-row'}
        >
          <div className={'me-auto tw-flex tw-items-center'}>
            <RevisionVersionTooltipPopover placement={'bottom'} />
            <UncontrolledDropdown
              className={'tw-flex tw-min-w-28 tw-rounded xl:tw-h-auto xl:tw-flex-col'}
            >
              <RevisionVersionControlButton tag={'span'}>
                {selectedRevision?.title || t('unset', { ns: 'common' })}
                <ChevronDown size={14} className={'ms-2'} />
              </RevisionVersionControlButton>
              <StyledDropdownMenu>
                {/* Create a copy & reverse to sort by desc */}
                {[...revisions].reverse().map((revision, i) => (
                  <StyledDropdownItem
                    key={i}
                    className={cn({
                      'active text-primary': revision.title === selectedRevision?.title,
                    })}
                    onClick={() => {
                      if (selectedRevision?.raw.id !== revision.raw.id) {
                        select(revision);
                      }
                    }}
                  >
                    {revision.title}
                  </StyledDropdownItem>
                ))}
              </StyledDropdownMenu>
            </UncontrolledDropdown>
          </div>

          {/* Primary actions: Download, Save, Share + Repurpose dropdown */}
          <If when={!!selectedRevisionMedia}>
            <div className="tw-ml-auto tw-flex tw-flex-wrap tw-items-center tw-gap-2">
              {/*
                Archived files get a dead control carrying the reason on hover
                instead of the dropdown (#2678): every item behind that dropdown
                resolves to a file that is not in hot storage, so opening it
                would offer two downloads that both fail.

                aria-disabled rather than disabled, for the same reason as the
                table's action — the button variants set `pointer-events: none`
                when disabled, which swallows the hover and would leave the
                customer with a dead control and no explanation.
              */}
              <If when={downloadBlocked}>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={'brand'}
                        size={'default'}
                        type={'button'}
                        aria-disabled={true}
                        className={'tw-cursor-not-allowed tw-gap-2 tw-text-sm tw-opacity-60'}
                        onClick={(e) => e.preventDefault()}
                      >
                        <CloudDownload size={16} />
                        <span className={'tw-hidden sm:tw-inline'}>{t('Download files')}</span>
                        <span className={'tw-inline sm:tw-hidden'}>{t('Download')}</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className={'tw-max-w-56 tw-text-left tw-text-sm'}>
                      {filesUnarchiving ? restoringMessage : archivedMessage}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </If>

              {/* Download - Primary action (brand color, larger) */}
              <If when={!downloadBlocked}>
                <UncontrolledDropdown>
                  <DropdownToggle
                    tag={Button}
                    variant="brand"
                    size="default"
                    className={'tw-gap-2 tw-text-sm'}
                  >
                    {isDownloading ? (
                      <Loader2 size={16} className={'tw-animate-spin'} />
                    ) : (
                      <CloudDownload size={16} />
                    )}
                    <span className={'tw-hidden sm:tw-inline'}>
                      {isDownloading ? t('Downloading...') : t('Download files')}
                    </span>
                    <span className={'tw-inline sm:tw-hidden'}>
                      {isDownloading ? t('Downloading...') : t('Download')}
                    </span>
                  </DropdownToggle>
                  <StyledDropdownMenu>
                    <StyledDropdownItem
                      onClick={async () => {
                        try {
                          await downloadAll([selectedRevisionMedia!]);
                          toast({
                            description: t('File downloaded successfully'),
                            variant: 'success',
                          });
                        } catch (error) {
                          toast({
                            description: t('Failed to download file'),
                            variant: 'destructive',
                          });
                        }
                      }}
                      disabled={!selectedRevisionMedia || isDownloading}
                    >
                      <CloudDownload size={12} className={'tw-me-2'} />
                      {selectedRevisionMedia?.file_name ?? '-'}
                    </StyledDropdownItem>
                    <StyledDropdownItem
                      onClick={async () => {
                        const allCollectionMedia = selectedRevision?.media
                          .map((m) => ({ ...m, collection_name: m.collection_name }))
                          .filter((m) =>
                            [
                              'default',
                              'resizes',
                              'thumbnails',
                              'captions',
                              'project_files',
                              'transcript',
                            ].includes(m.collection_name),
                          );
                        try {
                          await downloadAll(allCollectionMedia ?? []);
                          toast({
                            description: t('Files downloaded successfully'),
                            variant: 'success',
                          });
                        } catch (error) {
                          toast({
                            description: t('Failed to download files'),
                            variant: 'destructive',
                          });
                        }
                      }}
                      disabled={!selectedRevision?.hasMedia || isDownloading}
                    >
                      <CloudDownload size={12} className={'tw-me-2'} />
                      {t('All current version files')}
                    </StyledDropdownItem>
                  </StyledDropdownMenu>
                </UncontrolledDropdown>
              </If>

              {/* Separator between primary and secondary actions */}
              <div className="tw-hidden tw-h-6 tw-w-px tw-bg-border sm:tw-block" />

              {/* Save to Media Library - Secondary */}
              <If when={!!selectedRevisionMedia?.index_url}>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    'tw-gap-1 tw-text-xs md:tw-gap-2 md:tw-text-sm',
                    currentFileAlreadySaved && 'tw-opacity-60',
                  )}
                  disabled={isSaving || !selectedRevisionMedia || currentFileAlreadySaved}
                  onClick={async () => {
                    if (selectedRevisionMedia && !currentFileAlreadySaved) {
                      try {
                        await saveToMediaLibrary(selectedRevisionMedia);
                        setCurrentFileAlreadySaved(true);
                      } catch (error) {
                        console.error('Error saving to media library:', error);
                        setCurrentFileAlreadySaved(false);
                      }
                    }
                  }}
                >
                  {currentFileAlreadySaved ? <CheckCircle size={14} /> : <Save size={14} />}
                  <span className={'tw-hidden sm:tw-inline'}>
                    {currentFileAlreadySaved ? t('Already Saved') : t('Save to Media Library')}
                  </span>
                  <span className={'tw-inline sm:tw-hidden'}>
                    {currentFileAlreadySaved ? t('Saved') : t('Save')}
                  </span>
                  {isSaving && <ButtonLoader loading={isSaving} />}
                </Button>
              </If>

              {/* Share - Secondary */}
              <Button
                variant="outline"
                size="sm"
                className={'tw-gap-1 tw-text-xs md:tw-gap-2 md:tw-text-sm'}
                disabled={isLoadingShareUrl}
                onClick={openShareModal}
                title={t('Share video link') as string}
              >
                {isLoadingShareUrl ? (
                  <ButtonLoader loading={isLoadingShareUrl} />
                ) : (
                  <Share2 size={14} />
                )}
                <span className={'tw-hidden sm:tw-inline'}>{t('Share Video')}</span>
                <span className={'tw-inline sm:tw-hidden'}>{t('Share')}</span>
              </Button>

              {/* Repurpose dropdown - beN AI feature */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={
                      'tw-gap-1 tw-border-purple-300 tw-text-xs tw-text-purple-600 hover:tw-border-purple-500 hover:tw-bg-purple-50 md:tw-gap-2 md:tw-text-sm'
                    }
                  >
                    <Sparkles size={14} className="tw-text-purple-600" />
                    <span className={'tw-hidden sm:tw-inline'}>{t('Repurpose')}</span>
                    <span className={'tw-inline sm:tw-hidden'}>{t('Repurpose')}</span>
                    <ChevronDown size={12} />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="tw-w-auto tw-p-3">
                  <ContentGenerationButtons
                    mediaId={selectedRevisionMedia?.id ?? ''}
                    requestId={requestId}
                    className="tw-flex tw-flex-col tw-gap-1.5"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </If>
        </RevisionVideoControlRow>

        {/*Content main*/}
        <If
          when={!selectedRevision?.isFirstContact}
          else={
            <RevisionFallbackWrapper>
              <RevisionFallbackImage src={RevisionFileDisplayFallback} />
              <Text color={'gray'} $size={'md'} $md={'xl'} $centeredContent={true}>
                <WarningOctagonIcon className={'me-2'} />
                Create a video revision to add files
              </Text>
            </RevisionFallbackWrapper>
          }
        >
          {/* Revision is not first contact, render video player with selected revision media list */}
          <div className={'tw-flex tw-w-full tw-flex-col tw-gap-2 xl:tw-flex-row'}>
            <RevisionMediaList />

            <If
              when={selectedRevision?.hasMedia}
              else={
                <AspectRatio
                  ratio={16 / 9}
                  className={
                    'tw-flex tw-h-full tw-w-full tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-self-start'
                  }
                >
                  <RevisionFallbackImage src={RevisionFileDisplayFallback} />
                  <Text color={'gray'} $size={'md'} $md={'xl'} $centeredContent={true}>
                    <WarningOctagonIcon className={'me-2'} />
                    This revision has no files
                  </Text>
                </AspectRatio>
              }
            >
              {/* Storage state first, file type second — see restorationPlaceholder. */}
              <If
                when={!restorationPlaceholder}
                else={<div className={'tw-grow'}>{restorationPlaceholder}</div>}
              >
                {/*If media can't be played, render generic media viewed*/}
                <If
                  when={selectedRevisionMedia?.canPlay}
                  else={
                    <AspectRatio ratio={16 / 9}>
                      <MediaViewer
                        renderOriginal={true}
                        media={selectedRevisionMedia!}
                        className={
                          'tw-mx-auto !tw-h-full !tw-w-auto !tw-rounded-md !tw-object-contain'
                        }
                      />
                    </AspectRatio>
                  }
                >
                  <div className={'tw-grow'}>
                    <QueryErrorResetBoundary>
                      {({ reset }) => (
                        <ErrorBoundary
                          fallbackRender={({ error, resetErrorBoundary }: any) => (
                            <CardErrorFallback
                              error={error}
                              resetErrorBoundary={resetErrorBoundary}
                            />
                          )}
                          onReset={reset}
                        >
                          <React.Suspense fallback={<VideoPlayerSkeleton />}>
                            <MediaSignedUrlsQueryProvider id={selectedRevisionMedia!.id}>
                              <VideoPlayerContextProvider
                                meta={{
                                  fps: selectedRevisionMedia?.fps,
                                  durationInSeconds: selectedRevisionMedia?.durationInSeconds,
                                }}
                                defaultSrc={getPreferredSource(selectedRevisionMedia!)}
                                srcOptions={makeSourceOptions(selectedRevisionMedia!)}
                              >
                                <RevisionVideoPlayer />
                              </VideoPlayerContextProvider>
                            </MediaSignedUrlsQueryProvider>
                          </React.Suspense>
                        </ErrorBoundary>
                      )}
                    </QueryErrorResetBoundary>
                  </div>
                </If>
              </If>
            </If>
          </div>
        </If>

        {/*Content bottom — version navigation only */}
        <RevisionVideoControlRow className={'my-2 tw-justify-between'}>
          <RevisionCursorNavLink
            color={'primary'}
            disabled={!canSelectPrevious}
            onClick={selectPrevious}
            className={'tw-text-xs md:tw-text-sm'}
          >
            <ChevronLeftIcon color={'primary'} className={'me-1 md:me-2'} />
            <span className={'tw-hidden sm:tw-inline'}>{t('revisions.revision.action.prev')}</span>
            <span className={'tw-inline sm:tw-hidden'}>{t('Previous')}</span>
          </RevisionCursorNavLink>

          <RevisionCursorNavLink
            color={'primary'}
            disabled={!canSelectNext}
            onClick={selectNext}
            className={'tw-text-xs md:tw-text-sm'}
          >
            <span className={'tw-hidden sm:tw-inline'}>{t('revisions.revision.action.next')}</span>
            <span className={'tw-inline sm:tw-hidden'}>{t('Next')}</span>
            <ChevronRightIcon color={'primary'} className={'ms-1 md:ms-2'} />
          </RevisionCursorNavLink>
        </RevisionVideoControlRow>
      </RevisionMainContentCard>

      {/* Request revision chat */}
      <QueryErrorResetBoundary>
        {({ reset }) => (
          <ErrorBoundary
            fallbackRender={({ error, resetErrorBoundary }: any) => (
              <RevisionChatSection className={'lg:tw-col-span-5 xl:tw-col-span-4'}>
                <EmptySpaceFallbackImage className={'tw-mt-auto tw-self-center'} />
                <CardErrorFallback
                  className={'tw-mt-auto tw-w-full tw-border-none tw-shadow-none'}
                  error={error}
                  resetErrorBoundary={resetErrorBoundary}
                  description={t('alerts:error.failed_to_load_comments', {
                    defaultValue: 'Failed to load comments',
                  })}
                />
              </RevisionChatSection>
            )}
            onReset={reset}
          >
            <React.Suspense
              fallback={
                <Skeleton className={'tw-h-44 lg:tw-col-span-5 lg:tw-h-auto xl:tw-col-span-4'} />
              }
            >
              <RequestCommentsQueryContextProvider
                request={request.id}
                filters={{
                  limit: 50,
                  from_revision: selectedRevision?.id,
                }}
              >
                <RevisionChatSection className={'lg:tw-col-span-5 xl:tw-col-span-4'}>
                  <RevisionChatContent />
                </RevisionChatSection>
              </RequestCommentsQueryContextProvider>
            </React.Suspense>
          </ErrorBoundary>
        )}
      </QueryErrorResetBoundary>

      {/* Share Link Modal */}
      <Dialog open={isShareModalOpen} onOpenChange={setIsShareModalOpen}>
        <DialogContent className="tw-max-w-lg">
          <DialogHeader>
            <DialogTitle className="tw-flex tw-items-center tw-gap-2">
              <Share2 size={20} />
              {t('Share Video Link')}
            </DialogTitle>
          </DialogHeader>

          <div className="tw-space-y-4">
            <div className="tw-space-y-2">
              <label className="tw-text-sm tw-font-medium">{t('Video URL')}</label>
              <div className="tw-flex tw-gap-2">
                <Input
                  value={shareUrl}
                  readOnly
                  className="tw-flex-1 tw-font-mono tw-text-sm"
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                <Button
                  onClick={copyShareUrl}
                  variant={isCopied ? 'default' : 'outline'}
                  className={cn(
                    'tw-min-w-[100px]',
                    isCopied && 'tw-bg-green-600 hover:tw-bg-green-700',
                  )}
                >
                  {isCopied ? (
                    <>
                      <Check size={16} className="tw-mr-2" />
                      {t('Copied!')}
                    </>
                  ) : (
                    <>
                      <Clipboard size={16} className="tw-mr-2" />
                      {t('Copy')}
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className="tw-rounded-md tw-bg-blue-50 tw-p-3">
              <p className="tw-text-sm tw-text-blue-900">
                {t('Share this link with anyone to give them access to this video.')}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export { RevisionsContentMain };
